import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Factory, MapPin, Users as UsersIcon, LogOut, QrCode, Settings as SettingsIcon, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import LegalGate from "./LegalGate";
import SessionGuard from "./SessionGuard";

const NAV_ITEMS = [
  { to: "/app", label: "Máquinas", icon: Factory, end: true },
  { to: "/app/locations", label: "Ubicaciones", icon: MapPin },
  { to: "/app/users", label: "Usuarios", icon: UsersIcon, adminOnly: true },
  { to: "/app/settings", label: "Ajustes", icon: SettingsIcon, adminOnly: true },
  { to: "/platform", label: "Plataforma", icon: ShieldCheck, platformOnly: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const items = NAV_ITEMS.filter((i) => (!i.adminOnly || isAdmin) && (!i.platformOnly || user?.platform_admin));

  const handleLogout = async () => {
    navigate("/", { replace: true });
    await logout();
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <LegalGate user={user} />
      <SessionGuard />
      <header className="sticky top-0 z-40 glass-nav border-b border-zinc-200/60">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
          <NavLink to="/app" className="flex items-center gap-2.5 shrink-0" data-testid="nav-logo">
            <span className="h-9 w-9 rounded-xl bg-zinc-900 text-white flex items-center justify-center">
              <QrCode className="h-4.5 w-4.5" size={18} />
            </span>
            <span className="font-heading font-bold text-lg tracking-tight hidden xs:block sm:block">
              Asset Passport
            </span>
          </NavLink>

          <nav className="hidden md:flex items-center gap-1" data-testid="desktop-nav">
            {items.map(({ to, label, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={`nav-${label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                    isActive
                      ? "bg-zinc-900 text-white"
                      : "text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100"
                  }`
                }
              >
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-right">
              <p className="text-sm font-medium leading-tight" data-testid="nav-user-name">{user?.name}</p>
              <p className="text-xs text-zinc-400 leading-tight">{user?.company_name}</p>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              title="Cerrar sesión"
              className="h-10 w-10 rounded-full border border-zinc-200 bg-white flex items-center justify-center text-zinc-500 hover:text-zinc-900 hover:border-zinc-300 transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-zinc-950 focus-visible:ring-offset-2"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-8 sm:py-10 pb-28 md:pb-16">
        <Outlet />
      </main>

      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-40 glass-nav border-t border-zinc-200/60"
        data-testid="mobile-nav"
      >
        <div className="flex items-stretch justify-around px-2 py-2">
          {items.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`mobile-nav-${label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 px-5 py-1.5 rounded-2xl text-[11px] font-medium transition-colors duration-200 ${
                  isActive ? "text-zinc-950" : "text-zinc-400"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    className={`h-9 w-14 rounded-full flex items-center justify-center transition-colors duration-200 ${
                      isActive ? "bg-zinc-900 text-white" : ""
                    }`}
                  >
                    <Icon size={18} />
                  </span>
                  {label}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
